/**
 * Firebase Authentication Service
 * 
 * Handles all authentication operations including Google OAuth,
 * user session management, and auth state monitoring.
 */

import {
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  User,
  AuthError,
  UserCredential
} from 'firebase/auth';
import { auth } from './config';

// Authentication result types
export interface AuthResult {
  success: boolean;
  user?: User;
  error?: string;
}

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  emailVerified: boolean;
  isAnonymous: boolean;
  metadata: {
    creationTime: string | null;
    lastSignInTime: string | null;
  };
}

// Google OAuth provider configuration
const googleProvider = new GoogleAuthProvider();
googleProvider.addScope('profile');
googleProvider.addScope('email');

// Configure Google provider for optimal UX
googleProvider.setCustomParameters({
  prompt: 'select_account', // Always show account selector
});

/**
 * Sign in with Google OAuth
 */
export const signInWithGoogle = async (): Promise<AuthResult> => {
  try {
    console.log('🔐 Attempting Google sign-in...');
    
    const result: UserCredential = await signInWithPopup(auth, googleProvider);
    const user = result.user;
    
    if (!user) {
      throw new Error('No user returned from Google sign-in');
    }

    console.log('✅ Google sign-in successful:', {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName
    });

    return {
      success: true,
      user
    };
  } catch (error) {
    console.error('🚫 Google sign-in failed:', error);
    
    // Handle specific auth errors
    const authError = error as AuthError;
    let errorMessage = 'Sign-in failed. Please try again.';
    
    switch (authError.code) {
      case 'auth/popup-closed-by-user':
        errorMessage = 'Sign-in was cancelled. Please try again.';
        break;
      case 'auth/popup-blocked':
        errorMessage = 'Popup was blocked by browser. Please allow popups and try again.';
        break;
      case 'auth/network-request-failed':
        errorMessage = 'Network error. Please check your connection and try again.';
        break;
      case 'auth/too-many-requests':
        errorMessage = 'Too many failed attempts. Please try again later.';
        break;
      case 'auth/user-disabled':
        errorMessage = 'This account has been disabled. Please contact support.';
        break;
      default:
        errorMessage = authError.message || errorMessage;
    }

    return {
      success: false,
      error: errorMessage
    };
  }
};

/**
 * Sign out current user
 */
export const signOutUser = async (): Promise<AuthResult> => {
  try {
    console.log('🚪 Signing out user...');
    
    await signOut(auth);
    
    console.log('✅ Sign-out successful');
    return { success: true };
  } catch (error) {
    console.error('🚫 Sign-out failed:', error);
    
    return {
      success: false,
      error: 'Failed to sign out. Please try again.'
    };
  }
};

/**
 * Get current user
 */
export const getCurrentUser = (): User | null => {
  return auth.currentUser;
};

/**
 * Convert Firebase User to AuthUser format
 */
export const formatAuthUser = (user: User): AuthUser => {
  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    photoURL: user.photoURL,
    emailVerified: user.emailVerified,
    isAnonymous: user.isAnonymous,
    metadata: {
      creationTime: user.metadata.creationTime || null,
      lastSignInTime: user.metadata.lastSignInTime || null
    }
  };
};

/**
 * Auth state change listener
 */
export const onAuthStateChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  return !!auth.currentUser;
};

/**
 * Wait for auth to initialize
 */
export const waitForAuth = (): Promise<User | null> => {
  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      resolve(user);
    });
  });
};

/**
 * Get auth token for API requests
 */
export const getAuthToken = async (): Promise<string | null> => {
  try {
    const user = getCurrentUser();
    if (!user) return null;
    
    return await user.getIdToken();
  } catch (error) {
    console.error('🚫 Failed to get auth token:', error);
    return null;
  }
};

/**
 * Refresh auth token
 */
export const refreshAuthToken = async (): Promise<string | null> => {
  try {
    const user = getCurrentUser();
    if (!user) return null;
    
    return await user.getIdToken(true); // Force refresh
  } catch (error) {
    console.error('🚫 Failed to refresh auth token:', error);
    return null;
  }
};

// Development helpers
if (import.meta.env.DEV) {
  // Auth state debugging
  onAuthStateChanged(auth, (user) => {
    if (user) {
      console.log('🔐 Auth state: User signed in', {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName
      });
    } else {
      console.log('🔐 Auth state: User signed out');
    }
  });
}
